import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { User, ShoppingBag, Menu, X, ArrowRight } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import CartDrawer from '@/components/CartDrawer';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const LandingPage = () => {
  const navigate = useNavigate();
  const { getCartCount, setIsCartOpen } = useCart();
  const { currentUser, logout } = useAuth();
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const cartCount = getCartCount();

  const handleBadgeClick = () => {
    setClickCount((prev) => prev + 1);
    if (clickTimer) clearTimeout(clickTimer);
    const timer = setTimeout(() => setClickCount(0), 1000);
    setClickTimer(timer);
    if (clickCount + 1 === 3) {
      navigate('/admin/login');
      setClickCount(0);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleCategoryClick = (category) => {
    navigate(`/products?category=${category}`);
  };

  const handleCartClick = () => {
    if (!currentUser) {
      toast.info('Please login to view your cart');
      navigate('/login');
      return;
    }
    setIsCartOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <CartDrawer />
      
      {/* Premium Navbar with elegant styling */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-[1800px] mx-auto px-6 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="hidden md:flex items-center space-x-8 w-32"></div>

            <button 
              className="md:hidden text-gray-800"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            <div 
              className="absolute left-1/2 transform -translate-x-1/2 cursor-pointer"
              onClick={handleLogoClick}
              onDoubleClick={handleBadgeClick}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_luxury-estore-3/artifacts/czobo02e_B%26C%20LOGO%20-%2001.png" 
                alt="B&C Store" 
                className="h-10 sm:h-12 object-contain hover:opacity-80 transition-opacity"
              />
            </div>

            <div className="flex items-center space-x-5">
              {currentUser ? (
                <div className="relative">
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="text-gray-700 hover:text-black transition-colors flex items-center"
                  >
                    <User size={20} strokeWidth={1.5} />
                  </button>
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                      <button
                        onClick={() => { navigate('/profile'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light"
                      >
                        My Profile
                      </button>
                      <button
                        onClick={() => { navigate('/my-orders'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light"
                      >
                        My Orders
                      </button>
                      <button
                        onClick={() => { navigate('/products'); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 font-light"
                      >
                        Shop Now
                      </button>
                      <button
                        onClick={async () => { await logout(); setShowUserMenu(false); }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 font-light"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="text-gray-700 hover:text-black transition-colors"
                >
                  <User size={20} strokeWidth={1.5} />
                </button>
              )}
              
              <button 
                onClick={handleCartClick}
                className="text-gray-700 hover:text-black transition-colors relative"
              >
                <ShoppingBag size={20} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-light">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-6 py-4 space-y-4">
              {currentUser ? (
                <>
                  <button onClick={() => { navigate('/products'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">Shop Now</button>
                  <button onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">My Profile</button>
                  <button onClick={() => { navigate('/my-orders'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">My Orders</button>
                  <button onClick={async () => { await logout(); setMobileMenuOpen(false); }} className="block text-red-600 text-lg font-light">Logout</button>
                </>
              ) : (
                <button onClick={() => { navigate('/login'); setMobileMenuOpen(false); }} className="block text-gray-800 text-lg font-light">Sign In</button>
              )}
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section - Premium iPhone 17 Pro Max Background */}
      <section className="relative h-screen overflow-hidden">
        {/* Full Background Image - Premium iPhone */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1695048133142-1a20484d2569?q=80&w=2670&auto=format&fit=crop')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white/70 to-white/40" />
        </div>

        {/* Hero Content Overlay */}
        <div className="relative h-full flex items-end pb-20 px-6 lg:px-12 z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-light text-black leading-tight mb-4">
              Premium
              <br />
              <span className="italic">Accessories</span>
            </h1>
            <p className="text-black/60 text-base md:text-lg font-light max-w-md mb-6">
              Elevate your device experience with our curated collection of premium mobile accessories.
            </p>
            <button 
              onClick={() => navigate('/products')}
              className="px-8 py-4 bg-black text-white text-sm font-light tracking-widest hover:bg-gray-900 transition-colors"
            >
              Shop now
            </button>
          </motion.div>
        </div>
      </section>

      {/* Products Section - 4 Categories */}
      <section className="py-16 lg:py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-[1800px] mx-auto">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl md:text-2xl font-light mb-12 tracking-wide"
          >
            Electronics And Accessories
          </motion.h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {/* Screen Guard */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="group cursor-pointer"
              onClick={() => handleCategoryClick('Screen guard')}
            >
              <div className="relative aspect-square bg-white overflow-hidden mb-4 rounded-2xl border border-gray-100">
                <img
                  src="https://vaku.in/cdn/shop/files/Artboard1_b2f16677-c0e9-48d6-aeb5-0a5e4b6e675c_1024x1024.jpg?v=1742290207"
                  alt="Screen Guard"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-2xl"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 rounded-2xl" />
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <button className="w-full py-3 bg-white/95 backdrop-blur-sm text-black text-sm font-light tracking-wide hover:bg-white transition-colors rounded-lg">
                    View Collection
                  </button>
                </div>
              </div>
              <h3 className="text-sm font-light mb-1 tracking-wide">Screen Guard</h3>
              <p className="text-xs text-gray-500 font-light">Premium protection</p>
            </motion.div>

            {/* Camera Ring */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="group cursor-pointer"
              onClick={() => handleCategoryClick('Camera ring')}
            >
              <div className="relative aspect-square bg-white overflow-hidden mb-4 rounded-2xl border border-gray-100">
                <img
                  src="https://ultraglass.in/cdn/shop/files/FFE0604E-E25D-42EF-9B54-7FD3393FD474.jpg?v=1716190500"
                  alt="Camera Ring"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-2xl"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 rounded-2xl" />
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <button className="w-full py-3 bg-white/95 backdrop-blur-sm text-black text-sm font-light tracking-wide hover:bg-white transition-colors rounded-lg">
                    View Collection
                  </button>
                </div>
              </div>
              <h3 className="text-sm font-light mb-1 tracking-wide">Camera Ring</h3>
              <p className="text-xs text-gray-500 font-light">Lens protection</p>
            </motion.div>

            {/* Charger Cable */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="group cursor-pointer"
              onClick={() => handleCategoryClick('Charger cable')}
            >
              <div className="relative aspect-square bg-white overflow-hidden mb-4 rounded-2xl border border-gray-100">
                <img
                  src="https://makmobile.in/cdn/shop/products/E3B3C0D4-226C-4EF2-A86E-EE31D74E2503.png?v=1673284072"
                  alt="Charger Cable"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-2xl"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 rounded-2xl" />
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <button className="w-full py-3 bg-white/95 backdrop-blur-sm text-black text-sm font-light tracking-wide hover:bg-white transition-colors rounded-lg">
                    View Collection
                  </button>
                </div>
              </div>
              <h3 className="text-sm font-light mb-1 tracking-wide">Charger Cable</h3>
              <p className="text-xs text-gray-500 font-light">Fast charging</p>
            </motion.div>

            {/* Airpods */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="group cursor-pointer"
              onClick={() => handleCategoryClick('Airpods')}
            >
              <div className="relative aspect-square bg-white overflow-hidden mb-4 rounded-2xl border border-gray-100">
                <img
                  src="https://static.k-tuin.com/media/catalog/product/cache/1/image/0dc2d03fe217f8c83829496872af24a0/a/i/airpods-4-gen_1.jpg"
                  alt="Airpods"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 rounded-2xl"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 rounded-2xl" />
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                  <button className="w-full py-3 bg-white/95 backdrop-blur-sm text-black text-sm font-light tracking-wide hover:bg-white transition-colors rounded-lg">
                    View Collection
                  </button>
                </div>
              </div>
              <h3 className="text-sm font-light mb-1 tracking-wide">Airpods</h3>
              <p className="text-xs text-gray-500 font-light">Stylish protection</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Unmatched Quality Section */}
      <section className="relative min-h-screen">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://www.bcoutlet.in/cdn/shop/files/electronics-natural-6.jpg?v=1763053555&width=3840')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        </div>
        
        <div className="relative h-full min-h-screen flex items-center px-6 lg:px-12 py-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-light text-white mb-6 leading-[0.95]">
              <span className="italic">Unmatched</span>
              <br />
              quality
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-8 leading-relaxed font-light max-w-md">
              Our mobile accessories offer a perfect fusion of durability and elegance, enhancing your device experience.
            </p>
            <button 
              onClick={() => navigate('/products')}
              className="px-8 py-4 bg-[#FAF9F6] text-black text-sm font-light tracking-widest hover:bg-white transition-colors"
            >
              Shop now
            </button>
          </motion.div>
        </div>
      </section>

      {/* About Section - AirPods Pro Image */}
      <section className="py-20 lg:py-32 px-6 lg:px-12 bg-[#FAF9F6]">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <p className="text-xl md:text-2xl lg:text-3xl font-light leading-relaxed text-gray-800">
                Dedicated to elevating everyday experiences, our commitment is to provide premium mobile accessories that seamlessly blend functionality with style.
              </p>
              <button 
                onClick={() => navigate('/products')}
                className="inline-flex items-center text-black font-light tracking-wide group"
              >
                Shop now
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-2 transition-transform" strokeWidth={1.5} />
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="aspect-[4/3] overflow-hidden rounded-2xl"
            >
              <img
                src="https://www.macfactory.in/cdn/shop/products/20897-wireless-headphones-for-apple-airpods-pro-1.jpg?v=1642088645"
                alt="Premium AirPods Pro"
                className="w-full h-full object-cover rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Timeless Design Section */}
      <section className="relative min-h-screen">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://www.bcoutlet.in/cdn/shop/files/electronics-bold-1.jpg?v=1763094562&width=3840')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a2f2f]/90 to-[#1a2f2f]/50" />
        </div>
        
        <div className="relative h-full min-h-screen flex items-center px-6 lg:px-12 py-20">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <h2 className="text-5xl md:text-7xl lg:text-8xl font-light text-white mb-6 leading-[0.95]">
              Timeless
              <br />
              <span className="italic">design</span>
            </h2>
            <p className="text-white/70 text-base md:text-lg mb-8 font-light">
              Enhances device aesthetics effortlessly.
            </p>
            <button 
              onClick={() => navigate('/products')}
              className="px-8 py-4 bg-[#FAF9F6] text-black text-sm font-light tracking-widest hover:bg-white transition-colors"
            >
              Shop now
            </button>
          </motion.div>
        </div>
      </section>

      {/* Footer - Only on Landing Page */}
      <footer className="bg-[#FAF9F6] border-t border-gray-200/50">
        <div className="max-w-[1800px] mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {/* Connect */}
            <div>
              <h4 className="font-light tracking-wide mb-6 text-sm">Connect</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="text-gray-500 hover:text-black text-sm transition-colors font-light">Facebook</a>
                </li>
                <li>
                  <a href="#" className="text-gray-500 hover:text-black text-sm transition-colors font-light">Instagram</a>
                </li>
                <li>
                  <a href="mailto:contactus@bcstore.in" className="text-gray-500 hover:text-black text-sm transition-colors font-light">contactus@bcstore.in</a>
                </li>
              </ul>
            </div>

            {/* Terms and Policies */}
            <div>
              <h4 className="font-light tracking-wide mb-6 text-sm">Terms and Policies</h4>
              <ul className="space-y-3">
                <li>
                  <button 
                    onClick={() => navigate('/PrivacyPolicy')} 
                    className="text-gray-500 hover:text-black text-sm transition-colors font-light"
                  >
                    Privacy Policy
                  </button>
                </li>
                <li>
                  <button 
                    onClick={() => currentUser ? navigate('/my-orders') : navigate('/login')} 
                    className="text-gray-500 hover:text-black text-sm transition-colors font-light"
                  >
                    Track Order
                  </button>
                </li>
              </ul>
            </div>

            {/* Stay Inspired - Newsletter */}
            <div>
              <h4 className="font-light tracking-wide mb-6 text-sm">Stay Inspired</h4>
              <div className="flex max-w-md">
                <Input 
                  type="email" 
                  placeholder="Email address" 
                  className="flex-1 rounded-none border-0 border-b border-gray-300 bg-transparent focus:border-black focus-visible:ring-0 px-0 font-light placeholder:text-gray-400"
                />
                <button className="ml-4 text-black hover:opacity-70 transition-opacity">
                  <ArrowRight size={20} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="mt-16 pt-8 border-t border-gray-200/50 flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-black mb-4 md:mb-0 font-medium">
              B&C Store
            </p>
            <p className="text-xs text-gray-400 font-light">
              © 2025 B&C Store
            </p>
            <p className="text-xs text-gray-400 font-light mt-2 md:mt-0">
              Developed by{' '}
              <a href="https://venatriks.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                Venatriks
              </a>
              {' '}and{' '}
              <a href="https://doutly.com" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-black transition-colors">
                Doutly
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
